import { AlertTriangle, Loader2 } from "lucide-react";

import { formatScorecardScore } from "./utils";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ScorecardStaleBannerProps {
  message?: string;
  onRecalculate: () => void;
  onCancel?: () => void;
  isRecalculating?: boolean;
  isCancelling?: boolean;
  isDisabled?: boolean;
  actionLabel?: string;
  loadingLabel?: string;
  cancelLabel?: string;
  cancellingLabel?: string;
  variant?: "stale" | "first-run" | "running";
  progress?: { scoredRows: number; totalRows: number; partialScore?: number | null };
}

const ScorecardStaleBanner = ({
  message = "Stale — source cell values or configuration changed.",
  onRecalculate,
  onCancel,
  isRecalculating = false,
  isCancelling = false,
  isDisabled = false,
  actionLabel = "Re-run",
  loadingLabel = "Re-running…",
  cancelLabel = "Stop",
  cancellingLabel = "Stopping…",
  variant = "stale",
  progress,
}: ScorecardStaleBannerProps) => {
  const isRunning = variant === "running";
  const Icon = isRunning ? Loader2 : AlertTriangle;
  const canCancel = Boolean(onCancel);
  let buttonLabel = actionLabel;
  if (isCancelling) {
    buttonLabel = cancellingLabel;
  } else if (isRecalculating && canCancel) {
    buttonLabel = cancelLabel;
  } else if (isRecalculating) {
    buttonLabel = loadingLabel;
  }
  const handleAction = isRecalculating && onCancel ? onCancel : onRecalculate;
  const showProgress = isRunning && Boolean(progress) && (progress?.totalRows ?? 0) > 0;
  const progressPercent = showProgress
    ? Math.min(100, ((progress?.scoredRows ?? 0) / (progress?.totalRows ?? 1)) * 100)
    : 0;

  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-2 text-xs shadow-sm",
        isRunning
          ? "border-blue-200/70 bg-blue-50/70 text-blue-950"
          : "border-amber-200/70 bg-amber-50/70 text-amber-950",
      )}
    >
      <div className="flex items-center gap-2.5">
        <span
          className={cn(
            "flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white/80 ring-1",
            isRunning ? "text-blue-600 ring-blue-200/70" : "text-amber-600 ring-amber-200/70",
          )}
        >
          <Icon className={cn("h-3.5 w-3.5", isRunning && "animate-spin")} aria-hidden="true" />
        </span>
        <p className="min-w-0 flex-1 leading-5">{message}</p>
        <Button
          size="sm"
          variant="outline"
          className={cn(
            "ml-auto h-7 shrink-0 px-2.5 text-xs shadow-none",
            isRunning &&
              "border-blue-200 bg-white text-blue-700 hover:bg-blue-50 hover:text-blue-800",
            !isRunning && "border-amber-200 bg-white text-amber-800 hover:bg-amber-50",
          )}
          onClick={handleAction}
          disabled={isDisabled || isCancelling || (isRecalculating && !canCancel)}
        >
          {buttonLabel}
        </Button>
      </div>
      {showProgress && progress && (
        <div className="mt-2 space-y-1">
          <div className="h-1 w-full overflow-hidden rounded-full bg-blue-100/80">
            <div
              className="h-full rounded-full bg-blue-500 transition-[width] duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-[11px] tabular-nums text-blue-900/70">
            <span>
              {progress.scoredRows} of {progress.totalRows} rows scored
            </span>
            {typeof progress.partialScore === "number" && (
              <span>Avg score {formatScorecardScore(progress.partialScore)}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScorecardStaleBanner;
